import type { ChangeRequestByParentResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetChangeRequestByParentIdSubscription } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetChangeRequestByParentIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createSubscriptionHook } from 'src/utils';

import { mapChangeRequestRowsToFragment } from './mapChangeRequestRowsToFragment';

/**
 * The async Pending→Approved/Rejected flip is driven by OTHER actors (the merge
 * consumer / other approvers), so client-side invalidation can't observe it —
 * the tRPC feed is POLLED. `refetchOnWindowFocus` is globally off, so this poll
 * is the live mechanism. 5s keeps the panel responsive without hammering the
 * single SAM-local data-layer container in local/ephemeral envs.
 */
const POLL_INTERVAL_MS = 5000;

/**
 * Holding-pattern subscription hook for "all change requests of a parent" (any
 * status — backs the approval-levels panel, RSP-6087/6099).
 *
 * - `trpc` ON  → polls `frontend.changeRequest.changeRequestsByParent` and maps
 *   the V3 blob into the legacy (V2) `ChangeRequestParts` shape the panel was
 *   built against. The Hasura WebSocket subscription does not deliver in the V3
 *   stack, so the poll is the live transport.
 * - `trpc` OFF → the legacy Hasura `getChangeRequestByParentId` subscription.
 *
 * Built on `createSubscriptionHook` so that when the tRPC SSE transport lands
 * (RSP-4320) the poll → real-subscription swap happens in ONE place and every
 * consumer inherits it. Returns the factory's `{ data, loading, refetch, error }`
 * where `data.change_request` mirrors the subscription's array.
 */
export const useGetChangeRequestsByParentSubscription = createSubscriptionHook<
  { parentId: string },
  ChangeRequestByParentResponseRow[],
  GetChangeRequestByParentIdSubscription
>({
  trpcQueryOptions: (trpc, { parentId }) =>
    trpc.frontend.changeRequest.changeRequestsByParent.queryOptions({
      parentId,
    }),
  trpcRefetchInterval: () => POLL_INTERVAL_MS,
  mapTrpcDataToGraphQL: (rows) => ({
    change_request: mapChangeRequestRowsToFragment(rows),
  }),
  graphqlDocument: GetChangeRequestByParentIdDocument,
  graphqlVariables: ({ parentId }) => ({ Id: parentId }),
});
